// safetyPage/components/nearMiss/nearMissTable.js
// This is the component for the near miss table on the safety page, holding all near misses that are in draft status for users.

import { useState } from "react";
import styles from "../shared/safety-table-common.module.css";
import { ContextMenu } from "../../../common/ContextMenu";
import { buildCanonicalSafetyFormPath } from "../../../../services/SafetyFormRouteUtils";
import { useTableSort } from "../../../landingPage/components/del_table/useTableSort";
import { SortableHeader } from "../../../landingPage/components/del_table/SortableHeader";

// Near Miss Table
export const NearMissTable = ({
  nearMissList,
  onRowSelect,
  onDoubleClick,
  onDelete,
  selectedNearMissId,
}) => {
  // State for the context menu when user right clicks on a row
  const [contextMenu, setContextMenu] = useState(null);
  const { sortedItems, sortColumn, sortDirection, onSort } = useTableSort(nearMissList);

  const handleContextMenu = (e, nearMiss) => {
    e.preventDefault();
    onRowSelect(nearMiss);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      nearMiss: nearMiss,
    });
  };

  const getNearMissUrl = (nearMiss) => {
    return buildCanonicalSafetyFormPath({
      tab: "near_miss",
      nearMissId: nearMiss.id,
      action: "edit",
      open: true,
    });
  };

  const handleOpenInNewTab = (nearMiss) => {
    const url = getNearMissUrl(nearMiss);
    window.open(window.location.origin + url, "_blank");
  };

  const handleCopyLink = (nearMiss) => {
    const url = window.location.origin + getNearMissUrl(nearMiss);
    navigator.clipboard.writeText(url);
  };

  const getContextMenuOptions = (nearMiss) => [
    {
      label: "Open in new tab",
      action: () => handleOpenInNewTab(nearMiss),
    },
    {
      label: "Copy link",
      action: () => handleCopyLink(nearMiss),
    },
    {
      label: "Delete",
      action: () => onDelete(nearMiss.id),
    },
  ];

  return (
    <div className={styles.nearMissTableContainer} id="nearMissTable">
      {/* Card View (Mobile & Tablet) */}
      <div className={styles.cardContainer}>
        <div className={styles.cardList}>
          {nearMissList.map((nearMiss) => (
            <div
              key={`card-${nearMiss.id}`}
              onClick={() => onRowSelect(nearMiss)}
              onDoubleClick={() => onDoubleClick(nearMiss)}
              onContextMenu={(e) => handleContextMenu(e, nearMiss)}
              className={`${styles.card} ${
                nearMiss.id === selectedNearMissId ? styles.selectedCard : ""
              }`}
            >
              <div className={styles.cardContent}>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Created Date:</span>
                  <span className={styles.value}>{nearMiss.creation_date}</span>
                </div>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.label}>Updated Date:</span>
                <span className={styles.value}>{nearMiss.updated_date}</span>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.label}>Near Miss Date/Time:</span>
                <span className={styles.value}>
                  {nearMiss.incident_date} {nearMiss.incident_time}
                </span>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.label}>Location:</span>
                <span className={styles.value}>{nearMiss.location}</span>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.label}>Site:</span>
                <span className={styles.value}>{nearMiss.site}</span>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.label}>Reported To:</span>
                <span className={styles.value}>
                  {nearMiss.reported_to_name}
                </span>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.label}>Reported By:</span>
                <span className={styles.value}>
                  {nearMiss.reported_by_name}
                </span>
              </div>
              <div className={styles.cardActions}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(nearMiss.id);
                  }}
                  className={styles.deleteButton}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Table View (Desktop) */}
      <table className={`${styles.table} ${styles.nearMissTable}`}>
        <thead>
          <tr>
            <SortableHeader label="Created Date" column="created_at" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader label="Updated Date" column="updated_at" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader label="Near Miss Date" column="incident_date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <th>Near Miss Time</th>
            <th>Location</th>
            <th>Site</th>
            <th>Reported To</th>
            <th>Reported By</th>
            <th className={styles.deleteColumn}>Delete</th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((nearMiss) => (
            <tr
              key={`row-${nearMiss.id}`}
              onClick={() => onRowSelect(nearMiss)}
              onDoubleClick={() => onDoubleClick(nearMiss)}
              onContextMenu={(e) => handleContextMenu(e, nearMiss)}
              className={
                nearMiss.id === selectedNearMissId ? styles.selectedRow : ""
              }
            >
              <td title={nearMiss.created_date}>{nearMiss.created_at}</td>
              <td title={nearMiss.updated_date}>{nearMiss.updated_at}</td>
              <td title={nearMiss.incident_date}>{nearMiss.incident_date}</td>
              <td title={nearMiss.incident_time}>{nearMiss.incident_time}</td>
              <td title={nearMiss.location}>{nearMiss.location}</td>
              <td title={nearMiss.site}>{nearMiss.site}</td>
              <td title={nearMiss.reported_to_name}>
                {nearMiss.reported_to_name}
              </td>
              <td title={nearMiss.reported_by_name}>
                {nearMiss.reported_by_name}
              </td>
              <td className={styles.deleteColumn}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(nearMiss.id);
                  }}
                  className={styles.deleteButton}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          options={getContextMenuOptions(contextMenu.nearMiss)}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};
